const API = 'https://api.certificates.dev'
const TECH = 'laravel'
const opts: RequestInit = {
  credentials: 'include',
  headers: { Accept: 'application/json' },
}

export interface CountryCodeResponse {
  country_code: string
  country_region: string
  is_ppp: boolean
  level: number
  details: {
    country: string
    continent: string
  }
}

export interface PromotionalPrice {
  min_units: number
  promotional_price: number
}

export interface Bundle {
  slug: string
  name: string
  display_level_label: string
  full_price: number
  promotional_prices?: PromotionalPrice[]
  sort_order: number
  visible: boolean
}

export interface CatalogResponse {
  data: Bundle[]
  meta: {
    bulletpoints: string[]
    order: Record<string, unknown>
  }
}

export interface CheckoutResponse {
  link: string
}

export async function fetchCountryCode(): Promise<{
  region: string
  countryCode: string
}> {
  try {
    const response = await fetch(`${API}/country-code?tech=${TECH}`, opts)
    if (!response.ok) {
      throw new Error('Failed to fetch country code')
    }
    const data: CountryCodeResponse = await response.json()
    const region = data.is_ppp ? data.country_region : 'GLOBAL'
    return { region, countryCode: data.country_code }
  } catch (error) {
    console.error('Error fetching country code:', error)
    return { region: 'GLOBAL', countryCode: 'US' }
  }
}

export async function fetchCatalog(region: string): Promise<Bundle[]> {
  try {
    const response = await fetch(
      `${API}/catalog?tech=${TECH}&region=${region}`,
      opts,
    )
    if (!response.ok) {
      throw new Error('Failed to fetch catalog')
    }
    const data: CatalogResponse = await response.json()
    return data.data.filter((bundle) => bundle.visible !== false)
  } catch (error) {
    console.error('Error fetching catalog:', error)
    return []
  }
}

export async function createCheckout(
  slug: string,
  quantity: number,
  region: string,
): Promise<string | null> {
  try {
    const response = await fetch(`${API}/checkout?tech=${TECH}`, {
      ...opts,
      method: 'POST',
      headers: {
        ...opts.headers,
        'Content-Type': 'application/json',
        'X-Affiliate': 'laracert',
      },
      body: JSON.stringify({
        slug,
        quantity,
        region,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('Checkout error:', errorData)
      return null
    }

    const data: CheckoutResponse = await response.json()
    return data.link
  } catch (error) {
    console.error('Error creating checkout:', error)
    return null
  }
}

export function getPriceForQuantity(bundle: Bundle, quantity: number): number {
  if (!bundle.promotional_prices || bundle.promotional_prices.length === 0) {
    return bundle.full_price
  }

  const tier = bundle.promotional_prices
    .filter((p) => p.min_units <= quantity)
    .sort((a, b) => b.min_units - a.min_units)[0]

  return tier?.promotional_price ?? bundle.full_price
}
